import React, { memo, useState } from 'react';
import type { ICellRendererParams, IRowNode } from 'ag-grid-community';
import { Icon } from 'venn-ui-kit';
import { assertNotNil, getFrequencyType, useEventCallback, useHasFF, useModal } from 'venn-utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContentWithArrow, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import ProxyInfo from '../../modals/proxy-cell/ProxyInfo';
import {
  BulkManageAction,
  type BulkManageCashRow,
  type BulkManageFundRow,
  type BulkManageOptionRow,
  type BulkManageRow,
  type BulkManageUpdateFn,
} from '../types';
import { DeleteProxiesConfirmationModal } from '../DeleteProxiesConfirmationModal';
import { isBulkManageCashRow, isBulkManageOptionRow } from '../utils';

export type ProxyCellRendererParams = {
  canEditProxies: boolean;
  onUpdate: BulkManageUpdateFn;
  openBulkProxyModal: (node: IRowNode<BulkManageRow>) => void;
};

export function ProxyCellRenderer({
  node,
  canEditProxies,
  onUpdate,
  openBulkProxyModal,
}: ICellRendererParams<BulkManageFundRow | BulkManageCashRow | BulkManageOptionRow> & ProxyCellRendererParams) {
  // useEventCallback ensures that we never rerender the expensive proxy info section due to the functions or their dependencies changing.
  const launchBulkProxyModal = useEventCallback(() => openBulkProxyModal(node));
  const onRemove = useEventCallback(async () => {
    await onUpdate(BulkManageAction.FUND_MODIFIED, assertNotNil(node.data, 'Node data is required to remove proxy'));
  });

  if (isBulkManageCashRow(node.data)) {
    return <ProxyUnavailableMessage tooltipContent={`Proxying is unavailable for ${node.data.name}.`} />;
  }

  if (isBulkManageOptionRow(node.data)) {
    return <ProxyUnavailableMessage tooltipContent="Proxying is unavailable for options." />;
  }

  if (node?.group || !node.data?.investment) return null;

  const isCompositeBenchmark = node.data.investment.assetType === 'BENCHMARK';
  if (isCompositeBenchmark) {
    return <ProxyUnavailableMessage tooltipContent="Proxying is unavailable for composite benchmarks." />;
  }

  const nodeData = node.data;
  if (nodeData.fundProxyInfo?.proxyId) {
    return (
      <FundProxyInfoSection
        data={nodeData}
        canEditProxies={canEditProxies}
        onRemove={onRemove}
        launchBulkProxyModal={launchBulkProxyModal}
      />
    );
  }

  if (!canEditProxies) {
    return null;
  }

  if (node.data.secondary) {
    if (!node.data.isBenchmark) {
      return null;
    }
    return (
      <div className="flex gap-[6px] pr-[6px] items-center">
        <span className="text-venn-grey-80 grow shrink text-ellipsis overflow-clip whitespace-nowrap min-w-0">
          Set proxy on benchmark
        </span>
        <Button onClick={launchBulkProxyModal} variant="ghost" size="icon" className="size-max hover:bg-transparent">
          <Icon className="text-venn-highlight-dark hover:text-venn-highlight-light" type="circle-plus" prefix="far" />
        </Button>
      </div>
    );
  }

  return checkBoxToSetProxyMessage;
}

/** Component is memoized for performance; pass in only referentially stable data to prevent rerenders. */
const FundProxyInfoSection = memo(function _FundProxyInfoSection({
  data,
  canEditProxies,
  onRemove,
  launchBulkProxyModal,
}: {
  data: BulkManageFundRow;
  canEditProxies: boolean;
  onRemove: () => Promise<void>;
  launchBulkProxyModal: () => void;
}) {
  const layeredProxyPickerFF = useHasFF('layered_proxy_picker_ff');

  const { fundSecondProxyInfo, investment } = data;
  const fundProxyInfo = assertNotNil(data.fundProxyInfo);
  const investmentId = investment.id;
  const investmentFrequency = getFrequencyType(investment.returnFrequency);

  const [isDeleteConfirmationOpen, openDeleteConfirmation, closeDeleteConfirmation] = useModal();
  const [isProxiesUpdating, setIsProxiesUpdating] = useState(false);

  const modal = isDeleteConfirmationOpen && (
    <DeleteProxiesConfirmationModal
      fundsWithProxy={[investment]}
      proxyDataByFund={{
        [investmentId]: [fundProxyInfo],
      }}
      onCancel={closeDeleteConfirmation}
      onStartingUpdate={() => setIsProxiesUpdating(true)}
      onFinishedUpdate={() => {
        closeDeleteConfirmation();
        onRemove().finally(() => {
          setIsProxiesUpdating(false);
        });
      }}
    />
  );

  const label = fundSecondProxyInfo && layeredProxyPickerFF ? 'Multiple...' : fundProxyInfo.proxyName;
  const unproxiedStartRange =
    'unproxiedStartRange' in investment ? investment.unproxiedStartRange : fundProxyInfo.unproxiedStartDate;
  const unproxiedEndRange =
    'unproxiedEndRange' in investment ? investment.unproxiedEndRange : fundProxyInfo.unproxiedEndDate;

  return (
    <div className="flex gap-[6px] pr-[6px]">
      {modal}
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex grow shrink min-w-0 overflow-clip items-center gap-[3px]">
            <Icon type="arrows-left-right-to-line" className="text-xs text-venn-highlight-dark" />
            <div className="overflow-hidden whitespace-nowrap text-ellipsis text-venn-black">{label}</div>
          </div>
        </TooltipTrigger>

        <TooltipPortal>
          <TooltipContentWithArrow>
            <ProxyInfo
              key={investmentId}
              proxy={fundProxyInfo}
              investment={{
                id: investmentId,
                updated: investment.updated,
                frequency: 'frequency' in data ? getFrequencyType(data.frequency) : investmentFrequency,
                unproxiedStartRange,
                unproxiedEndRange,
                startRange: data.startDate,
                endRange: data.endDate,
              }}
            />
          </TooltipContentWithArrow>
        </TooltipPortal>
      </Tooltip>
      {canEditProxies && (
        <div className="flex gap-[6px] items-center">
          <Button
            onClick={launchBulkProxyModal}
            variant="ghost"
            size="icon"
            className="size-max text-venn-highlight-dark hover:text-venn-highlight-light hover:bg-transparent"
            disabled={isProxiesUpdating}
          >
            <Icon type="edit" />
          </Button>
          <Button
            onClick={openDeleteConfirmation}
            variant="ghost"
            size="icon"
            className="size-max text-venn-grey-50 hover:text-venn-error hover:bg-transparent"
            disabled={isProxiesUpdating}
          >
            <Icon type="circle-x" />
          </Button>
        </div>
      )}
    </div>
  );
});

const ProxyUnavailableMessage = memo(function _ProxyUnavailableTooltip({
  tooltipContent,
}: {
  tooltipContent: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger className="flex items-center gap-[3px] h-full">
        <div className="text-venn-grey-80">Proxying Unavailable</div>{' '}
        <Icon type="circle-info" className="text-venn-grey-50" />
      </TooltipTrigger>

      <TooltipPortal>
        <TooltipContentWithArrow>{tooltipContent}</TooltipContentWithArrow>
      </TooltipPortal>
    </Tooltip>
  );
});

const checkBoxToSetProxyMessage = <div className="text-venn-grey-50">Check box to set a proxy</div>;
